// Pure logic for "Map -> Create Journey". No React and no I/O.
//
// The server's reverse-geocoding answer (GET /tsp/geocoding/reverse):
//   { latitude, longitude, status: "RESOLVED" | "PARTIAL" | "NOT_FOUND",
//     address: { state, district, city, area } | null,          provider names, for reading
//     place: { state_id, state_name, district_id, district_name,
//              city_id, city_name, kind: "city" | "area" } | null, TSP's own places
//     areas: ["Narpoli"], source }
//
// What the screens need from it: the popup's rows and message, and the route
// stop the Add Journey form starts with.

import { PLACE_KIND } from "../../utils/tspPlace";
import { formatCoordinate } from "../../utils/tspCoordinates";

export const GEOCODE_STATE = Object.freeze({
  idle: "idle",
  loading: "loading",
  resolved: "resolved",
  partial: "partial",
  notFound: "notFound",
  failed: "failed",
  invalid: "invalid",
  forbidden: "forbidden",
});

// Fixed wording only: a provider's or server's own error text is never shown.
const MESSAGES = {
  [GEOCODE_STATE.loading]: "Finding the location…",
  [GEOCODE_STATE.partial]: "Only part of this location could be identified. You can complete it on the Add Journey form.",
  [GEOCODE_STATE.notFound]: "No address was found at this point. The latitude and longitude are kept; choose the place on the Add Journey form.",
  [GEOCODE_STATE.failed]: "Location details could not be found right now. The latitude and longitude are kept; you can try again or choose the place on the Add Journey form.",
  [GEOCODE_STATE.invalid]: "This point is not a valid location. Select another point on the map.",
  [GEOCODE_STATE.forbidden]: "You do not have permission to create journeys.",
};

// { loading, error, data } of the request (useTspApiResource) -> one state.
export function geocodeState({ point, loading, error, data }) {
  if (!point) return GEOCODE_STATE.idle;
  if (loading) return GEOCODE_STATE.loading;
  if (error) {
    const status = error.response?.status;
    if (status === 400) return GEOCODE_STATE.invalid;
    if (status === 401 || status === 403) return GEOCODE_STATE.forbidden;
    return GEOCODE_STATE.failed;
  }
  if (!data) return GEOCODE_STATE.loading;
  if (data.status === "RESOLVED") return GEOCODE_STATE.resolved;
  if (data.status === "PARTIAL") return GEOCODE_STATE.partial;
  return GEOCODE_STATE.notFound;
}

export const geocodeMessage = (state) => MESSAGES[state] || "";

// May "Create Journey" be offered for this state? Always once the lookup has
// settled — even without an address the point itself is kept — except for a
// point the server refused as invalid (or a caller without permission).
export const canCreateFrom = (state) => ![GEOCODE_STATE.idle, GEOCODE_STATE.loading, GEOCODE_STATE.invalid, GEOCODE_STATE.forbidden].includes(state);

// The popup rows. TSP's matched names first (what the journey will be saved
// under), else the provider's, else nothing.
export function popupRows(point, data) {
  const place = data?.place || {};
  const address = data?.address || {};
  const city = place.city_name || address.city || null;
  // The area is the locality INSIDE the city/town — never the same name again.
  const same = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
  const area = (data?.areas || [])[0] || (address.area && !same(address.area, city) ? address.area : null);
  return [
    { key: "state", label: "State", value: place.state_name || address.state || null },
    { key: "district", label: "District", value: place.district_name || address.district || null },
    { key: "city", label: "City / Taluka / Town", value: city },
    { key: "area", label: "Area", value: area },
    { key: "latitude", label: "Latitude", value: point ? formatCoordinate(point.latitude) : null },
    { key: "longitude", label: "Longitude", value: point ? formatCoordinate(point.longitude) : null },
  ];
}

// The place the Add Journey picker understands (utils/tspPlace.js), as far as
// it is known. Ids are strings (they are <select> values).
export function placeFromGeocode(data) {
  const place = data?.place;
  if (!place || !place.state_id) return null;
  const known = {
    stateId: String(place.state_id),
    state: place.state_name || "",
    districtId: place.district_id ? String(place.district_id) : "",
    district: place.district_name || "",
    cityId: place.kind === PLACE_KIND.city && place.city_id ? String(place.city_id) : "",
    city: place.district_id ? place.city_name || "" : "",
    kind: place.kind === PLACE_KIND.city && place.city_id ? PLACE_KIND.city : PLACE_KIND.area,
  };
  return known;
}

// A complete place can go straight onto the route; anything less is handed to
// the picker to finish.
export const isCompletePlace = (place) => Boolean(place && place.stateId && place.districtId && place.city);

// Everything the Add Journey form starts with for a point picked on the map:
//   { point, place (or null), complete, areas }
export function mapSelectionForForm(point, data) {
  const place = placeFromGeocode(data);
  return { point, place, complete: isCompletePlace(place), areas: data?.areas || [] };
}

// Areas typed as "Narpoli, Kalher" -> ["Narpoli", "Kalher"] (the API's list), and back.
export const AREA_MAX = 100;
export function parseAreasText(text) {
  const seen = new Set();
  return String(text || "")
    .split(/[,\n]/)
    .map((area) => area.replace(/\s+/g, " ").trim())
    .filter((area) => {
      const key = area.toLowerCase();
      if (!area || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}
export const areasToText = (areas) => (areas || []).join(", ");
